// buraeu/src/pages/UserCreation/components/ProfileViaForm.tsx

import React, { useState, ChangeEvent, useEffect } from "react";
import { toast } from "react-toastify";
import { getRequest, postWithFormData } from "../../../helpers/api.helper";
import { FaSave, FaUserCircle, FaCamera, FaArrowLeft } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import {
  heightOptions,
  totalPropertyValueOptions,
} from "../../../utils/constants";
import { ProfileViaData } from "../types/ProfileViaData";
import { useLanguage } from "../../../context/LanguageContext";
import { EMPLOYMENT_OPTIONS } from "../../../utils/employmentOptions";
import ProfileImageCropModal from "../../../components/ProfileImageCropModal";
import VoiceMessageField from "../../../components/VoiceMessageField";
import { uploadVoiceMessage } from "../../../helpers/voiceMessage.helper";
import { CREATED_BY_OPTIONS, formatCreatedBy } from "../../../utils/createdBy";
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/input";

interface ProfileFormProps {
  initialData?: ProfileViaData;
  bureauId: string;
  isMediatorProfile?: boolean;
  onBack: () => void;
  onSuccess: (userData?: any) => void;
  variant?: "modal" | "page";
  headerActions?: React.ReactNode;
}

const ProfileViaForm: React.FC<ProfileFormProps> = ({
  initialData,
  bureauId,
  isMediatorProfile = false,
  onBack,
  onSuccess,
  variant = "modal",
  headerActions,
}) => {
  const isPage = variant === "page";
  const { t } = useLanguage();
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);

  const [imagePreview, setImagePreview] = useState<string>("");

  const [rawImage, setRawImage] = useState<File | null>(null); // original upload
  const [image, setImage] = useState<File | null>(null); // cropped final

  const [showImageCrop, setShowImageCrop] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceFile, setVoiceFile] = useState<File | null>(null);
  const [uploadedVoiceUrl, setUploadedVoiceUrl] = useState("");
  const [isVoiceRecording, setIsVoiceRecording] = useState(false);

  const [profileData, setProfileData] = useState<ProfileViaData>({
    gender: "",
    createdBy: isMediatorProfile ? "other_mediator" : "self",
    citizenship: "Indian",
    countryCode: "+91", // ✅ DEFAULT
    mobileNumber: "",
    password: "",
    fullName: "",
    dateOfBirth: "",
    birthTime: "",
    maritalStatus: "",
    height: "",
    religion: "",
    caste: "",
    subCaste: "",
    education: "",
    employment: "",
    occupation: "",
    annualIncome: "",
    fatherOccupation: "",
    motherOccupation: "",
    totalPropertyValue: "",
    profileDescription: "",
    raasi: "",
    nakshatram: "",
    gothram: "",
    placeOfBirth: "",
    country: "",
    state: "",
    district: "",
  });

  const generatePassword = () => {
    const digits = "0123456789";
    let password = "";

    for (let i = 0; i < 8; i++) {
      password += digits.charAt(Math.floor(Math.random() * digits.length));
    }

    return password;
  };

  useEffect(() => {
    setProfileData((prev) => ({
      ...prev,
      password: prev.password || generatePassword(),
    }));
  }, []);

  useEffect(() => {
    if (!initialData) return;

    setProfileData((prev) => ({
      ...prev,
      ...initialData,
      password: initialData.password || prev.password || generatePassword(),
      countryCode: initialData.countryCode || "+91",
      createdBy:
        initialData.createdBy ||
        (isMediatorProfile ? "other_mediator" : "self"),
    }));
  }, [initialData, isMediatorProfile]);

  const religionOptions = [
    { value: "hindu", label: "Hindu" },
    { value: "muslim", label: "Muslim" },
    { value: "christian", label: "Christian" },
    { value: "sikh", label: "Sikh" },
    { value: "buddhist", label: "Buddhist" },
  ];
  const citizenshipOptions = [
    { value: "Indian", label: "Indian Profile" },
    { value: "Nri", label: "NRI Profile" },
  ];
  const createdByOptions = [...CREATED_BY_OPTIONS];

  const [castes, setCastes] = useState<any[]>([]);
  const [subCastes, setSubCastes] = useState<any[]>([]);
  const [occupations, setOccupations] = useState<any[]>([]);
  const [educationList, setEducationList] = useState<any[]>([]);
  const [incomeList, setIncomeList] = useState<any[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const mapOptions = (arr: any[], key: string) => {
    return arr.map((item) => ({
      value: item[key], // 👈 TEXT
      label: item[key],
    }));
  };

  const loadData = async () => {
    const res = await getRequest("/common/all");
    if (res.success) {
      setCastes(mapOptions(res.data.caste, "caste"));
      setSubCastes(mapOptions(res.data.subCaste, "sub_caste"));
      setOccupations(mapOptions(res.data.occupation, "occupation"));
      setEducationList(mapOptions(res.data.education, "education"));
      setIncomeList(mapOptions(res.data.annualIncome, "annual_income"));
      setCountries(mapOptions(res.data.country, "country"));
      setStates(mapOptions(res.data.state, "state"));
      setDistricts(mapOptions(res.data.city, "city"));
    }
  };

  const getModalTitle = (key: string) => {
    if (key === "totalPropertyValue") return "Total Property Value";
    if (key === "country") return "Country";
    if (key === "state") return "State";
    if (key === "district") return "District";
    return key.replace(/_/g, " ");
  };

  const maritalStatusOptions = [
    { value: "neverMarried", label: "Never Married" },
    { value: "awaitingDivorce", label: "Awaiting Divorce" },
    { value: "divorced", label: "Divorced" },
    { value: "widow", label: "Widow / Widower" },
  ];

  const handleInputChange = (field: keyof ProfileViaData, value: string) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateAge = (dob: string) => {
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }

    return age;
  };

  const validateDOB = (dob: string) => {
    if (!dob) return "Date of birth is required";

    const birthDate = new Date(dob);
    const today = new Date();

    if (birthDate > today) {
      return "Date of birth cannot be in the future";
    }

    const age =
      today.getFullYear() -
      birthDate.getFullYear() -
      (today <
      new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate())
        ? 1
        : 0);

    if (age < 18) {
      return "Age must be at least 18 years";
    }

    return "";
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (isVoiceRecording) {
      toast.error("Please stop voice recording before saving the profile");
      return;
    }

    const data = {
      ...profileData,
      fullName: profileData.fullName?.trim(),
      caste: profileData.caste?.trim(),
      maritalStatus: profileData.maritalStatus?.trim(),
    };
    // 🔴 Full Name
    if (!data.fullName.trim()) {
      toast.error("Please enter full name");
      return;
    }
    if (!rawImage) {
      toast.error("Please upload profile image");
      return;
    }

    if (!image) {
      setPendingSubmit(true);
      setShowImageCrop(true);
      return;
    }

    // 🔴 Gender
    if (!data.gender) {
      toast.error("Please select gender");
      return;
    }
    if (!data.createdBy) {
      toast.error("Please select created by");
      return;
    }
    if (!data.citizenship) {
      toast.error("Please select citizenship");
      return;
    }
    if (!data.countryCode) {
      toast.error("Please select country code");
      return;
    }

    // 🔴 Mobile Number
    if (!data.mobileNumber) {
      toast.error("Please enter mobile number");
      return;
    }
    if (data.mobileNumber.length !== 10) {
      toast.error("Please enter valid 10 digit mobile number");
      return;
    }

    if (!/^\d{10}$/.test(data.mobileNumber)) {
      toast.error("Mobile number must be exactly 10 digits");
      return;
    }

    // 🔴 Caste
    if (!data.caste) {
      toast.error("Please select caste");
      return;
    }

    // 🔴 Marital Status
    if (!data.maritalStatus) {
      toast.error("Please select marital status");
      return;
    }

    // 🔴 Location validations
    if (!data.country) {
      toast.error("Please select country");
      return;
    }

    if (!data.state) {
      toast.error("Please select state");
      return;
    }

    if (!data.district) {
      toast.error("Please select district");
      return;
    }

    const dobError = validateDOB(data.dateOfBirth);

    if (dobError) {
      toast.error(dobError);
      return;
    }

    if (!data.height) {
      toast.error("Please select height");
      return;
    }

    if (!data.religion) {
      toast.error("Please select religion");
      return;
    }

    if (!data.subCaste) {
      toast.error("Please select sub caste");
      return;
    }

    if (!data.education) {
      toast.error("Please select education");
      return;
    }

    if (!data.employment) {
      toast.error("Please select employment status");
      return;
    }

    if (!data.occupation) {
      toast.error("Please select occupation");
      return;
    }

    if (!data.annualIncome) {
      toast.error("Please select annual income");
      return;
    }

    const formData = new FormData();
    const appendOptionalValue = (key: string, value: unknown) => {
      if (value === undefined || value === null) {
        formData.append(key, "");
        return;
      }

      const text = String(value).trim();
      formData.append(key, text);
    };
    // append image FIRST or anywhere before submit
    formData.append("image", image);
    // Append all profile data fields to FormData
    formData.append("gender", data.gender || "");
    formData.append("createdBy", data.createdBy || "");
    formData.append("citizenship", data.citizenship || "");
    formData.append("countryCode", data.countryCode);
    formData.append("mobileNumber", data.mobileNumber);

    formData.append("password", data.password || "");
    formData.append("fullName", data.fullName || "");
    formData.append("dateOfBirth", data.dateOfBirth || "");
    formData.append("birthTime", data.birthTime || "");
    formData.append("maritalStatus", data.maritalStatus || "");
    formData.append("height", data.height || "");
    formData.append("religion", data.religion || "");
    formData.append("caste", data.caste || "");
    formData.append("subCaste", data.subCaste || "");
    formData.append("education", data.education || "");
    formData.append("employment", data.employment || "");
    formData.append("occupation", data.occupation || "");
    formData.append("annualIncome", data.annualIncome || "");
    formData.append("fatherOccupation", data.fatherOccupation || "");
    formData.append("motherOccupation", data.motherOccupation || "");
    appendOptionalValue("totalBrothers", data.totalBrothers);
    appendOptionalValue("youngerBrothers", data.youngerBrothers);
    appendOptionalValue("elderBrothers", data.elderBrothers);
    appendOptionalValue("marriedBrothers", data.marriedBrothers);
    appendOptionalValue("totalSisters", data.totalSisters);
    appendOptionalValue("youngerSisters", data.youngerSisters);
    appendOptionalValue("elderSisters", data.elderSisters);
    appendOptionalValue("marriedSisters", data.marriedSisters);
    formData.append("totalPropertyValue", data.totalPropertyValue || "");
    formData.append("profileDescription", data.profileDescription || "");
    formData.append("raasi", data.raasi || "");
    formData.append("nakshatram", data.nakshatram || "");
    formData.append("gothram", data.gothram || "");
    formData.append("placeOfBirth", data.placeOfBirth || "");
    formData.append("bureauId", bureauId);
    formData.append("visibleToAll", "false");
    formData.append("isMediatorProfile", String(isMediatorProfile));
    formData.append("country", data.country);
    formData.append("state", data.state);
    formData.append("district", data.district);
    formData.append("step1", "1");
    formData.append("step2", "1");
    formData.append("step3", "1");
    formData.append("step4", "1");
    formData.append("step5", "1");
    formData.append("step6", "1");
    formData.append("step7", "1");
    formData.append("step8", "1");
    formData.append("step9", "1");
    formData.append("step10", "1");

    try {
      setIsSubmitting(true);
      let voiceMessage = uploadedVoiceUrl;
      if (voiceFile) {
        const uploaded = await uploadVoiceMessage(voiceFile, data.mobileNumber);
        setUploadedVoiceUrl(uploaded.fileUrl);
        voiceMessage = uploaded.fileUrl;
      }
      formData.append("voiceMessage", voiceMessage || "");
      const res = await postWithFormData("/user/register", formData);
      if (res.success) {
        toast.success("Your profile is successfully completed");
        onSuccess(res.data?.user ?? res.data);
      } else {
        toast.error(
          res.message || "Failed to create profile. Please try again.",
        );
      }
    } catch (error) {
      console.error("Error creating profile:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (imagePreview) URL.revokeObjectURL(imagePreview);

    setRawImage(file); // store original
    setShowImageCrop(true); // open crop modal
  };

  const ProfileImageUpload = () => (
    <div className="flex flex-col items-center">
      <input
        type="file"
        accept="image/*"
        id="profileImage"
        onChange={handleImageSelect}
        className="hidden"
      />

      <label htmlFor="profileImage" className="relative cursor-pointer group">
        {imagePreview ? (
          <img
            src={imagePreview}
            alt="Profile Preview"
            className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-md hover:opacity-80 transition"
          />
        ) : (
          <div className="w-32 h-32 rounded-full bg-gray-100 flex items-center justify-center border border-gray-300 text-gray-400 hover:bg-gray-200 transition relative overflow-hidden">
            <FaUserCircle size={80} className="text-gray-300" />
          </div>
        )}

        <div className="absolute bottom-0 right-0 bg-red-500 p-2 rounded-full text-white shadow-lg group-hover:bg-red-600 transition">
          <FaCamera size={16} />
        </div>
      </label>

      <p className="text-sm text-gray-500 mt-3 text-center">
        {t("Click the icon to upload your photo")}{" "}
        <span className="text-red-500">*</span>
      </p>
    </div>
  );

  const openDropdownModal = (key: keyof ProfileViaData, options: any[]) => {
    setModalData({ key, options });
    setSearchTerm("");
  };

  const handleSelect = (option: any) => {
    handleInputChange(modalData.key, option.value);
    setModalData(null);
    setSearchTerm("");
  };

  const getMaxDob18Plus = () => {
    const today = new Date();

    const maxDate = new Date(today);
    maxDate.setFullYear(today.getFullYear() - 18);

    const year = maxDate.getFullYear();
    const month = String(maxDate.getMonth() + 1).padStart(2, "0");
    const day = String(maxDate.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const maxDob = getMaxDob18Plus();

  return (
    <>
      {isSubmitting && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-xl px-8 py-6 flex flex-col items-center gap-4 shadow-xl">
            <div className="w-12 h-12 border-4 border-gray-300 border-t-green-600 rounded-full animate-spin" />
            <p className="text-sm font-medium text-gray-700">
              {t("Saving profile, please wait")}...
            </p>
          </div>
        </div>
      )}

      <div
        className={
          isPage
            ? "w-full"
            : "fixed inset-0 bg-black/50 flex justify-center items-center z-50 overflow-y-auto"
        }
      >
        <div
          className={
            isPage
              ? "w-full bg-white md:rounded-2xl md:border md:border-gray-100 md:shadow-sm"
              : "bg-white rounded-lg w-full max-w-4xl my-8 mx-4 shadow-lg max-h-[90vh] overflow-y-auto"
          }
        >
          <div className={isPage ? "px-4 pb-6 pt-2 md:p-8" : "p-6 sm:p-8"}>
            <div className="flex items-center justify-between mb-6">
              {isPage ? (
                <div />
              ) : (
                <button
                  onClick={onBack}
                  disabled={isSubmitting}
                  className="text-blue-600 hover:underline text-sm"
                >
                  <FaArrowLeft size={20} />
                </button>
              )}
              {headerActions ? <div>{headerActions}</div> : <div />}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
              <div className="lg:pt-2">
                <div
                  className={
                    isPage
                      ? "rounded-2xl border border-gray-100 bg-gray-50 p-5"
                      : ""
                  }
                >
                  <ProfileImageUpload />
                </div>
              </div>
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-6 px-2 ${
                  isPage
                    ? "max-h-none overflow-visible"
                    : "max-h-[60vh] overflow-y-auto"
                }`}
              >
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Gender")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() =>
                      openDropdownModal("gender", [
                        { value: "Male", label: "Male" },
                        { value: "Female", label: "Female" },
                      ])
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.gender || t("Select Gender")}
                  </button>
                </div>
                {!isMediatorProfile && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t("Created By")} <span className="text-red-500">*</span>
                    </label>
                    <button
                      onClick={() =>
                        openDropdownModal("createdBy", createdByOptions)
                      }
                      disabled={isMediatorProfile}
                      className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                    >
                      {formatCreatedBy(profileData.createdBy) ||
                        "Select Creator"}
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Citizenship")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() =>
                      openDropdownModal("citizenship", citizenshipOptions)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.citizenship || "Select Citizenship"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {isMediatorProfile
                      ? t("Enter Mediator Contact Details")
                      : t("Enter Customer Contact Details")}{" "}
                    *
                  </label>
                  <div className="flex min-w-0 items-stretch gap-2 overflow-x-hidden">
                    {/* Country Code */}
                    <select
                      value={profileData.countryCode}
                      onChange={(e) =>
                        handleInputChange("countryCode", e.target.value)
                      }
                      className="w-24 shrink-0 border border-gray-300 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-red-400 sm:w-28"
                    >
                      {getCountries().map((country) => (
                        <option
                          key={country}
                          value={`+${getCountryCallingCode(country)}`}
                        >
                          +{getCountryCallingCode(country)}
                        </option>
                      ))}
                    </select>

                    {/* Mobile Number */}
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={profileData.mobileNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "mobileNumber",
                          e.target.value.replace(/\D/g, ""),
                        )
                      }
                      maxLength={10}
                      className="min-w-0 w-0 flex-1 rounded-lg border border-gray-300 p-2 focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter 10-digit mobile number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Full Name")} *
                  </label>
                  <input
                    type="text"
                    value={profileData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Date of Birth")} *
                    {profileData.dateOfBirth && (
                      <span className="text-sm text-gray-500 mt-1 ml-2">
                        (Age: {calculateAge(profileData.dateOfBirth)})
                      </span>
                    )}
                  </label>
                  <input
                    type="date"
                    value={profileData.dateOfBirth}
                    max={maxDob}
                    onChange={(e) =>
                      handleInputChange("dateOfBirth", e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum age required: 18 years
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Marital Status")}
                    <span className="text-red-500"> *</span>
                  </label>

                  <button
                    onClick={() =>
                      openDropdownModal("maritalStatus", maritalStatusOptions)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {maritalStatusOptions.find(
                      (o) => o.value === profileData.maritalStatus,
                    )?.label || "Select Marital Status"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("height")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => openDropdownModal("height", heightOptions)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.height || "Select Height"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("religion")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() =>
                      openDropdownModal("religion", religionOptions)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {religionOptions.find(
                      (r) => r.value === profileData.religion,
                    )?.label || "Select Religion"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("caste")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => openDropdownModal("caste", castes)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.caste || "Select Caste"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("sub_caste")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => openDropdownModal("subCaste", subCastes)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.subCaste || "Select Sub Caste"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("education")}/{t("Qualification")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() =>
                      openDropdownModal("education", educationList)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.education || "Select Education"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Employment Status")}{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={profileData.employment}
                    onChange={(e) =>
                      handleInputChange("employment", e.target.value)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Status</option>
                    {EMPLOYMENT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("occupation")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => openDropdownModal("occupation", occupations)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.occupation || "Select Occupation"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("income")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() =>
                      openDropdownModal("annualIncome", incomeList)
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {profileData.annualIncome || "Select Annual Income"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Total Property Value")}
                  </label>

                  <button
                    onClick={() =>
                      openDropdownModal(
                        "totalPropertyValue",
                        totalPropertyValueOptions,
                      )
                    }
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {totalPropertyValueOptions.find(
                      (o) => o.value === profileData.totalPropertyValue,
                    )?.label ||
                      profileData.totalPropertyValue ||
                      "Select Property Value"}
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Country")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    onClick={() => openDropdownModal("country", countries)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white"
                  >
                    {countries.find((c) => c.value === profileData.country)
                      ?.label || "Select Country"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("State")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    disabled={!profileData.country}
                    onClick={() => openDropdownModal("state", states)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white disabled:bg-gray-100"
                  >
                    {states.find((s) => s.value === profileData.state)?.label ||
                      "Select State"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("District")} <span className="text-red-500">*</span>
                  </label>
                  <button
                    disabled={!profileData.state}
                    onClick={() => openDropdownModal("district", districts)}
                    className="w-full p-2 border border-gray-300 rounded-lg text-left bg-white disabled:bg-gray-100"
                  >
                    {districts.find((d) => d.value === profileData.district)
                      ?.label || "Select District"}
                  </button>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Profile Description/Preference")}
                  </label>
                  <textarea
                    value={profileData.profileDescription}
                    onChange={(e) =>
                      handleInputChange("profileDescription", e.target.value)
                    }
                    placeholder="Write detailed profile description..."
                    className="w-full p-3 border border-gray-300 rounded-lg h-56 resize-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <VoiceMessageField
                    initialUrl={uploadedVoiceUrl}
                    showSaveButton={false}
                    onRecordingChange={setIsVoiceRecording}
                    onFileChange={(file) => {
                      setVoiceFile(file);
                      setUploadedVoiceUrl("");
                    }}
                    handleSaveVoiceMessage={() => {}}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div
              className={`mt-6 flex gap-4 px-2 ${
                isPage ? "border-t border-gray-100 pt-4" : ""
              }`}
            >
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || isVoiceRecording}
                className={`flex-1 py-3 px-6 rounded-lg flex items-center justify-center transition duration-300
                ${
                  isSubmitting || isVoiceRecording
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-lg"
                }
              `}
              >
                <FaSave className="mr-2" />
                {isSubmitting ? "Saving..." : "Save"}
              </button>
              <button
                onClick={onBack}
                disabled={isSubmitting || isVoiceRecording}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-400 transition duration-300"
              >
                {t("Cancel")}
              </button>
            </div>
          </div>
        </div>

        {modalData && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                onClick={() => {
                  setModalData(null);
                  setSearchTerm("");
                }}
              >
                <AiOutlineClose size={22} />
              </button>

              <h2 className="text-lg font-bold mb-4 text-center">
                {t("Select")} {getModalTitle(modalData.key)}
              </h2>

              <input
                type="text"
                placeholder={`Search ${modalData.key}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
              />

              <div className="max-h-64 overflow-y-auto divide-y">
                {modalData.options
                  .filter(
                    (o: any) =>
                      o.label
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                      o.value.toLowerCase().includes(searchTerm.toLowerCase()),
                  )
                  .map((o: any) => (
                    <button
                      key={o.value}
                      onClick={() => handleSelect(o)}
                      className="block w-full text-left px-4 py-2 hover:bg-red-50"
                    >
                      {o.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showImageCrop && rawImage && (
        <ProfileImageCropModal
          sourceFile={rawImage}
          onConfirm={(croppedFile: File) => {
            setImage(croppedFile);

            const preview = URL.createObjectURL(croppedFile);
            setImagePreview(preview);

            setShowImageCrop(false);

            if (pendingSubmit) {
              setPendingSubmit(false);
              handleSubmit();
            }
          }}
          onClose={() => {
            setShowImageCrop(false);
            setPendingSubmit(false);
          }}
        />
      )}
    </>
  );
};

export default ProfileViaForm;
